import { useRef } from 'react';
import type React from 'react';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import {
	useCommentToScriptArm,
	useDisableVideoCreation,
	usePsfContactDecisionMakerExperiment,
	useSupporterVoicesCommentToScriptExperiment,
	useSupporterVoicesR2Arm,
	useSupporterVoicesVideoFirstExperiment,
	useTrackPsfContactDecisionMakerExposure,
	useVideoFirstAskArm,
} from '#src/app/pages/actions/shared/hooks';
import type { CommentToScriptArm, SupporterVoicesR2Arm, VideoFirstAskArm } from '#src/app/pages/actions/shared/hooks';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { AddSupporterVoiceThankYou } from './components/AddSupporterVoiceThankYou';
import { AddYourVideoInvite } from './components/AddYourVideoInvite';
import { CommentToScriptWelcome } from './components/CommentToScriptWelcome';
import { ContactDecisionMakerPaintedDoor } from './components/ContactDecisionMakerPaintedDoor';
import { StaticFacesAsk } from './components/StaticFacesAsk';
import { VideoFirstAsk } from './components/VideoFirstAsk';
import { VideoFirstConsolidatedAsk } from './components/VideoFirstConsolidatedAsk';

export const Welcome: React.FC = () => {
	const [{ shareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	return shareSupporterVoiceSuccess ? <PostCommentWelcome /> : <VideoAskWelcome />;
};

const VideoAskWelcome: React.FC = () => {
	const disableVideoCreation = useDisableVideoCreation();
	const { experiment } = useSupporterVoicesVideoFirstExperiment();
	// Resolves the arm, today's petition and viewer constraints included: a petition with no video ask,
	// or a viewer who cannot record, falls back to today's invite. The same hook decides whether the
	// action card is drawn around this screen, so the two can never disagree.
	const videoFirstAskArm = useVideoFirstAskArm();
	// R2 (CHANGE-99359): resolved the same way, but only V3 has a screen of its own - V1/V2 are
	// copy-only diffs `AddYourVideoInvite` reads for itself.
	const r2Arm = useSupporterVoicesR2Arm();

	// Petition-level relevance gate: with no video ask surface the experiment isn't relevant, so render
	// today's invite with no exposure. This is the ONLY code gate — GrowthBook can't target petition
	// facts; logged-in/mobile targeting and allocation stay dashboard-owned.
	if (disableVideoCreation) {
		return <AddYourVideoInvite />;
	}

	return (
		<>
			{/* Fires $exposure on mount for real assignments; no-ops on fallback/override/empty key.
			    Control arms fire too (control is the baseline arm for lift analysis). */}
			<TrackExperimentExposure experiment={experiment} />
			<VideoAskScreen arm={videoFirstAskArm} r2Arm={r2Arm} />
		</>
	);
};

/**
 * Split by screen rather than by copy. `v2_consolidated` folds recording into the ask, so it is a
 * different screen; v1 and v3 are one screen with two sets of words, and the narrower prop type on
 * `VideoFirstAsk` is what stops that pair drifting apart.
 *
 * R2's V3 (`v3_static_faces`) is checked ahead of R1's arm: the two experiments' variation unions
 * overlap only in `undefined` (control/gated), so at most one of `arm`/`r2Arm` is ever set, but this
 * keeps V3 explicit rather than relying on that.
 */
const VideoAskScreen: React.FC<{ arm: VideoFirstAskArm | undefined; r2Arm: SupporterVoicesR2Arm | undefined }> = ({
	arm,
	r2Arm,
}) => {
	if (r2Arm === 'v3_static_faces') {
		return <StaticFacesAsk />;
	}

	if (arm === 'v2_consolidated') {
		return <VideoFirstConsolidatedAsk />;
	}

	return arm ? <VideoFirstAsk arm={arm} /> : <AddYourVideoInvite />;
};

const PostCommentWelcome: React.FC = () => {
	useTrackPsfContactDecisionMakerExposure();
	const { isTreatment } = usePsfContactDecisionMakerExperiment();
	// Focus returns here when the painted-door entry modal is dismissed (WCAG 2.4.3, CHANGE-96802).
	const thankYouRef = useRef<HTMLDivElement>(null);

	const { experiment: commentToScriptExperiment } = useSupporterVoicesCommentToScriptExperiment();
	const { arm: commentToScriptArm, isRelevant: isCommentToScriptRelevant } = useCommentToScriptArm();

	return (
		<>
			{isCommentToScriptRelevant ? (
				<>
					<TrackExperimentExposure experiment={commentToScriptExperiment} />
					<PostCommentScreen arm={commentToScriptArm} thankYouRef={thankYouRef} />
				</>
			) : (
				<AddSupporterVoiceThankYou ref={thankYouRef} />
			)}
			{isTreatment && <ContactDecisionMakerPaintedDoor restoreFocusRef={thankYouRef} />}
		</>
	);
};

const PostCommentScreen: React.FC<{
	arm: CommentToScriptArm | undefined;
	thankYouRef: React.RefObject<HTMLDivElement | null>;
}> = ({ arm, thankYouRef }) =>
	arm ? <CommentToScriptWelcome arm={arm} ref={thankYouRef} /> : <AddSupporterVoiceThankYou ref={thankYouRef} />;
